import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { AudiencePicker } from "./AudiencePicker";

const normG = (g) => (g || "").replace(/^grupo\s+/i, "").trim();
export const libroProgramas = (l) => (l.programas && l.programas.length ? l.programas : [l.programa || "Todos"]);
export const libroGrupos = (l) => (l.grupos && l.grupos.length ? l.grupos : [normG(l.grupo) || "Todos"]);
export const libroDestino = (l) => {
  const ps = libroProgramas(l), gs = libroGrupos(l);
  return `${ps.includes("Todos") ? "Todos los programas" : ps.join(", ")} · ${gs.includes("Todos") ? "Todos los grupos" : gs.map((g) => `Grupo ${g}`).join(", ")}`;
};

export const LibroAudienceDialog = ({ libro, mode, onClose, onSaved }) => {
  const [f, setF] = useState({ programas: [], grupos: [], mes: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (libro) setF({ programas: libroProgramas(libro), grupos: libroGrupos(libro), mes: libro.mes || "" }); }, [libro]);
  const dup = mode === "duplicate";
  const save = async () => {
    if (!f.programas.length || !f.grupos.length) { toast.error("Elige al menos un programa y un grupo."); return; }
    if (!f.mes) { toast.error("El mes es obligatorio."); return; }
    setSaving(true);
    try {
      if (dup) await api.post(`/admin/campus/libros/${libro.id}/duplicar`, f);
      else await api.put(`/admin/campus/libros/${libro.id}`, f);
      toast.success(dup ? "Libro duplicado para el nuevo destino." : "Destino del libro actualizado.");
      onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!libro} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="libro-audience-dialog">
        <DialogHeader>
          <DialogTitle>{dup ? "Duplicar libro" : "Editar destino del libro"} · {libro?.titulo}</DialogTitle>
          <DialogDescription>{dup ? "Se crea una copia (con sus preguntas y puntos) para los programas y grupos que elijas. El original no cambia." : "Solo verán este libro los alumnos de los programas y grupos marcados."}</DialogDescription>
        </DialogHeader>
        <div className="max-w-xs"><Label className="mb-1 block font-bold text-xs">Mes</Label><Input data-testid="libro-dialog-mes" type="month" value={f.mes} onChange={(e) => setF((p) => ({ ...p, mes: e.target.value }))} /></div>
        <AudiencePicker testid="libro-dialog-aud" programas={f.programas} grupos={f.grupos} onChange={(v) => setF((p) => ({ ...p, ...v }))} />
        <div className="flex justify-end gap-2 pt-2">
          <button data-testid="libro-dialog-cancel" onClick={onClose} className="rounded-xl ring-1 ring-black/10 px-5 py-2 font-bold text-sm">Cancelar</button>
          <button data-testid="libro-dialog-save" onClick={save} disabled={saving} className="rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold px-6 py-2 text-sm disabled:opacity-60 inline-flex items-center gap-2">{saving && <Loader2 className="w-4 h-4 animate-spin" />}{dup ? "Duplicar" : "Guardar cambios"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
